import api from "@/plugins/api";
/**
 * Searches OPROJ projects by name/code via GET /portal/projects/search (server pagination),
 * optionally restricted to lifecycle statuses.
 */
export async function searchProjects(q, page = 1, pageSize = 10, status, options = {}) {
    const response = await api.get("/portal/projects/search", {
        params: {
            q,
            page,
            pageSize,
            status,
            includeFunding: options.includeFunding ? true : undefined,
        },
        // CONSTRAINT: `status` is an array; axios' default spelling `status[]=A` is not bound by
        // ASP.NET Core, so the BFF would answer 200 and silently drop the filter (R61).
        paramsSerializer: { indexes: null },
    });
    const body = response.data;
    const items = Array.isArray(body?.data) ? body.data : [];
    return {
        items,
        totalItems: body?.pagination?.totalItems ?? items.length,
        totalPages: body?.pagination?.totalPages ?? 1,
    };
}
/**
 * Creates a project via POST /portal/projects. Validation errors carry the backend's dotted
 * `errorCode`s (resolved by the caller through `resolveApiErrorKey`).
 */
export async function createProject(payload) {
    const response = await api.post("/portal/projects", payload);
    const created = response.data?.data;
    if (!created) {
        throw new Error("Empty response from project creation");
    }
    return created;
}
/**
 * Gets a project's full details via GET /portal/projects/{id}.
 */
export async function getProject(projectId) {
    const response = await api.get(`/portal/projects/${projectId}`);
    const project = response.data?.data;
    if (!project) {
        throw new Error("Empty response from project lookup");
    }
    return project;
}
/**
 * Updates a project via PUT /portal/projects/{id} (full replacement of its editable fields).
 * Validation errors carry the backend's dotted `errorCode`s (resolved by the caller through
 * `resolveApiErrorKey`).
 */
export async function updateProject(projectId, payload) {
    const response = await api.put(`/portal/projects/${projectId}`, payload);
    const project = response.data?.data;
    if (!project) {
        throw new Error("Empty response from project update");
    }
    return project;
}
/**
 * Creates a milestone via POST /portal/projects/{id}/milestones.
 */
export async function createMilestone(projectId, payload) {
    const response = await api.post(`/portal/projects/${projectId}/milestones`, payload);
    const milestone = response.data?.data;
    if (!milestone) {
        throw new Error("Empty response from milestone creation");
    }
    return milestone;
}
/**
 * Updates a milestone via PUT /portal/projects/{id}/milestones/{milestoneId}.
 */
export async function updateMilestone(projectId, milestoneId, payload) {
    const response = await api.put(`/portal/projects/${projectId}/milestones/${milestoneId}`, payload);
    const milestone = response.data?.data;
    if (!milestone) {
        throw new Error("Empty response from milestone update");
    }
    return milestone;
}
/**
 * Deletes a milestone via DELETE /portal/projects/{id}/milestones/{milestoneId}.
 */
export async function deleteMilestone(projectId, milestoneId) {
    await api.delete(`/portal/projects/${projectId}/milestones/${milestoneId}`);
}
/**
 * Gets a project's funding figures and one page of its donations via
 * GET /portal/projects/{id}/donations, filtered and ordered per `query`. A project with no
 * donation answers 200 with zero totals and an empty list — there is no 404 on this route.
 */
export async function getProjectDonations(projectId, query) {
    const response = await api.get(`/portal/projects/${projectId}/donations`, {
        params: {
            limit: query.pageSize,
            page: query.page,
            // axios drops undefined params, so an absent filter/sort is not sent and the backend
            // applies its own default (no filter, newest first).
            donor: query.donor || undefined,
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
        },
    });
    const body = response.data;
    const summary = body?.data;
    return {
        items: summary?.items ?? [],
        totalDonations: summary?.totalDonations ?? 0,
        totalAllocated: summary?.totalAllocated ?? 0,
        matchingDonations: summary?.matchingDonations ?? summary?.totalDonations ?? 0,
        // Paging lives in the envelope, not the payload, and the SERVED values win over what we asked
        // for — the backend clamps both.
        page: body?.pagination?.page ?? query.page,
        pageSize: body?.pagination?.perPage ?? query.pageSize,
    };
}
/**
 * Gets the campaigns linked to a project via GET /portal/projects/{id}/campaigns, most recent
 * first. A project no campaign has claimed answers 200 with an empty list — there is no 404 on
 * this route either.
 */
export async function getProjectCampaigns(projectId, limit = 3) {
    const response = await api.get(`/portal/projects/${projectId}/campaigns`, { params: { limit } });
    const summary = response.data?.data;
    return {
        items: summary?.items ?? [],
        totalCampaigns: summary?.totalCampaigns ?? 0,
    };
}
